import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import PageHero from "../components/PageHero";
import { MapPin, BedDouble, ArrowUpRight } from "lucide-react";
import { publicApi } from "../admin/api";
import { needsInvert, resolveImage } from "../lib/data";
import SEO from "../components/SEO";
import { useSettings } from "../lib/useSettings";

const DEFAULT_HERO = "";

const FILTERS = [
  { key: "all", label: "Tutti" },
  { key: "4", label: "4 stelle" },
  { key: "3", label: "3 stelle" },
];

export default function Hotels() {
  const [filter, setFilter] = useState("all");
  const [hotels, setHotels] = useState([]);
  const settings = useSettings();
  const heroImg = settings?.hero_hotels || DEFAULT_HERO;

  useEffect(() => {
    publicApi.get("/hotels").then((r) => setHotels(r.data)).catch(() => {});
  }, []);

  const list = useMemo(() => {
    if (filter === "all") return hotels;
    return hotels.filter((h) => String(h.stars) === filter);
  }, [filter, hotels]);

  return (
    <div data-testid="hotels-page">
      <SEO
        title="Hotel a Milano"
        path="/hotel"
        description="14 strutture alberghiere 3 e 4 stelle nel cuore e nei nodi chiave di Milano. Esplora il portafoglio Invest Hospitality."
        image={heroImg}
      />
      <PageHero
        eyebrow={settings?.hero_hotels_eyebrow || "Portafoglio"}
        title={settings?.hero_hotels_title || "I nostri *hotel.*"}
        subtitle={
          settings?.hero_hotels_subtitle ||
          "Quattordici strutture alberghiere nel cuore e nei nodi chiave di Milano. Ognuna con la propria identità, tutte con lo stesso standard operativo."
        }
        image={heroImg}
      />

      <section className="relative py-16 md:py-24 px-6 md:px-12 max-w-[1440px] mx-auto">
        <div className="flex items-center justify-between flex-wrap gap-4 mb-10">
          <div className="flex gap-2">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                data-testid={`hotel-filter-${f.key}`}
                onClick={() => setFilter(f.key)}
                className={`px-5 py-2.5 rounded-full text-sm border transition-colors ${
                  filter === f.key
                    ? "bg-white text-black border-white"
                    : "border-white/15 text-white/70 hover:text-white hover:border-white/30"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-white/50">
            {list.length} strutture
          </span>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {list.map((h, i) => (
            <motion.div
              key={h.id}
              initial={false}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.5,
                delay: Math.min(i * 0.04, 0.4),
                ease: [0.22, 1, 0.36, 1],
              }}
              data-testid={`hotel-card-${h.slug}`}
            >
              <Link
                to={`/hotel/${h.slug}`}
                className="group block rounded-2xl overflow-hidden border border-white/10 bg-[#0F0F14] hover:border-white/25 transition-colors h-full"
              >
                <div className="relative aspect-[16/10] overflow-hidden bg-gradient-to-br from-[#1a1218] via-[#0F0F14] to-[#0a0a0d]">
                  {resolveImage(h.image) ? (
                    <div
                      className="absolute inset-0 bg-cover bg-center transition-transform duration-[1.2s] group-hover:scale-110"
                      style={{ backgroundImage: `url(${resolveImage(h.image)})` }}
                    />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center opacity-30">
                      <BedDouble size={48} className="text-[#C48F56]" />
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                  <div className="absolute top-4 left-4 glass rounded-full px-3 py-1 text-[10px] uppercase tracking-[0.2em] text-[#C48F56]">
                    {"★".repeat(h.stars)} stelle
                  </div>
                  {h.logo && resolveImage(h.logo) && (
                    <img
                      src={resolveImage(h.logo)}
                      alt={h.name}
                      className={`absolute bottom-4 right-4 h-8 w-auto max-w-[100px] object-contain opacity-90 ${
                        needsInvert(h.logo) ? "brightness-0 invert" : ""
                      }`}
                    />
                  )}
                </div>
                <div className="p-7">
                  <h3 className="font-display text-2xl md:text-3xl tracking-tight">
                    {h.name}
                  </h3>
                  <p className="mt-1 text-white/50 italic text-sm">{h.tagline}</p>
                  <p className="mt-4 text-white/60 text-sm leading-relaxed line-clamp-3">
                    {h.description}
                  </p>
                  <div className="mt-6 flex items-center justify-between pt-5 border-t border-white/5">
                    <div className="flex items-center gap-4 text-xs text-white/50">
                      <span className="inline-flex items-center gap-1.5">
                        <MapPin size={13} />
                        {h.area}
                      </span>
                      <span className="inline-flex items-center gap-1.5">
                        <BedDouble size={13} />
                        {h.rooms} camere
                      </span>
                    </div>
                    <span className="h-8 w-8 rounded-full border border-white/20 grid place-items-center group-hover:bg-[#C48F56] group-hover:border-[#C48F56] group-hover:text-black transition-colors">
                      <ArrowUpRight size={12} />
                    </span>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>
    </div>
  );
}
